/* Outcome tag picker — shown on the close stage. The bot writes a default
 * outcome at finalize time (saved if accept_offer fired, else unresolved);
 * this card lets the human agent confirm or correct it. PATCHes the call
 * row's `outcome` field — same field the existing reports already key on. */

import { useState } from "react";

import { API_URL } from "../lib/api";
import { agentHeaders, useAgentIdentity } from "./identity";
import { type Outcome } from "./useCallStage";

const OUTCOME_LABEL: Record<Outcome, string> = {
  saved:      "Saved",
  churned:    "Churned",
  escalated:  "Escalated",
  unresolved: "Unresolved",
};

const OUTCOME_HINT: Record<Outcome, string> = {
  saved:      "Customer accepted an offer or chose to stay.",
  churned:    "Customer cancelled or churned despite the call.",
  escalated:  "Routed to a human supervisor or specialist.",
  unresolved: "Call ended without a clear outcome — follow up needed.",
};

const OUTCOMES: Outcome[] = ["saved", "churned", "escalated", "unresolved"];

export function OutcomeCard({
  callId,
  outcome,
}: {
  callId: number;
  outcome: Outcome | null;
}) {
  const { identity } = useAgentIdentity();
  const [busy, setBusy] = useState<Outcome | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function set(next: Outcome) {
    if (busy) return;
    setBusy(next);
    setErr(null);
    try {
      const r = await fetch(`${API_URL}/api/calls/${callId}`, {
        method: "PATCH",
        headers: {
          "content-type": "application/json",
          ...agentHeaders(identity),
        },
        body: JSON.stringify({ outcome: next }),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => "");
        setErr(text || `outcome update failed (${r.status})`);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <article id="agent-desk-outcome-card" className="agent-desk-outcome-card">
      <div className="agent-desk-outcome-eyebrow">CALL OUTCOME</div>
      <div className="agent-desk-outcome-headline">
        {outcome
          ? `Tagged as "${OUTCOME_LABEL[outcome]}"`
          : "Tag the outcome"}
      </div>
      <p className="agent-desk-outcome-body">
        {outcome
          ? "Confirm or change the resolution. The reports key off this field."
          : "How did the call resolve? The reports key off this tag."}
      </p>

      <div className="agent-desk-outcome-options">
        {OUTCOMES.map((opt) => {
          const isCurrent = opt === outcome;
          const isBusy = busy === opt;
          return (
            <button
              key={opt}
              id={`agent-desk-outcome-pick-${opt}`}
              type="button"
              className={`agent-desk-outcome-pick is-${opt} ${isCurrent ? "is-current" : ""}`}
              onClick={() => set(opt)}
              disabled={busy !== null}
              title={OUTCOME_HINT[opt]}
            >
              {isBusy ? "…" : OUTCOME_LABEL[opt]}
            </button>
          );
        })}
      </div>

      {err && <div className="dial-err">{err}</div>}
    </article>
  );
}
